import { SupabaseService } from './SupabaseService';
import { supabase } from '../../config/supabase';
import { 
  Admin, 
  AdminLoginRequest, 
  AdminLoginResponse, 
  AdminWithPermissions,
  AdminWithRoles,
  AdminApiResponse,
  GroupedPermissions
} from '../../types/admin.types';

/**
 * AdminAuthService - Service xử lý authentication và authorization cho admin
 */
export class AdminAuthService extends SupabaseService {
  private static adminTableName = 'admin';
  private static permissionTableName = 'permission';
  private static adminPermissionTableName = 'admin_permissions';

  /**
   * Đăng nhập admin
   */
  static async loginAdmin(credentials: AdminLoginRequest): Promise<AdminApiResponse<AdminLoginResponse>> {
    try {
      // Tìm admin theo username
      const { data: adminData, error: adminError } = await this.findWhere<Admin>(
        this.adminTableName,
        [{ column: 'user_name', operator: 'eq', value: credentials.user_name }],
        { limit: 1 }
      );

      if (adminError) {
        return {
          data: null,
          error: adminError.message,
          success: false
        };
      }

      if (!adminData || adminData.length === 0) {
        return {
          data: null,
          error: 'Tài khoản không tồn tại',
          success: false
        };
      }

      const admin = adminData[0];

      // Kiểm tra mật khẩu (trong thực tế nên sử dụng bcrypt để hash)
      if (admin.pass_word !== credentials.pass_word) {
        return {
          data: null,
          error: 'Mật khẩu không đúng',
          success: false
        };
      }

      // Lấy roles và permissions của admin
      const rolesResult = await this.getAdminRolesAndPermissions(admin.id!);

      if (!rolesResult.success) {
        return {
          data: null,
          error: 'Không thể tải quyền của admin',
          success: false
        };
      }

      // Convert roles thành backward compatible permissions
      const permissions = this.convertRolesToPermissions(admin, rolesResult.data || []);

      // Loại bỏ password khỏi response
      const { pass_word, ...adminWithoutPassword } = admin;

      return {
        data: {
          admin: adminWithoutPassword as Admin,
          roles: rolesResult.data || [],
          permissions: permissions,
          token: this.generateAdminToken(admin)
        },
        error: null,
        success: true
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message,
        success: false
      };
    }
  }

  /**
   * Lấy quyền của admin thông qua roles
   */
  static async getAdminRolesAndPermissions(adminId: string): Promise<AdminApiResponse<AdminWithRoles['roles']>> {
    try {
      console.log('Fetching roles for admin ID:', adminId);
      
      // Query để lấy admin với roles và permissions
      const { data, error } = await supabase
        .from('admin_roles')
        .select(`
          admin_id,
          roles!inner (
            id,
            name,
            description,
            role_permissions (
              module_name,
              sub_module_name,
              view_permission,
              add_permission,
              edit_permission,
              delete_permission
            )
          )
        `)
        .eq('admin_id', adminId);

      if (error) {
        console.error('Error fetching admin roles and permissions:', error);
        return {
          data: null,
          error: error.message,
          success: false
        };
      }

      console.log('Raw query result:', JSON.stringify(data, null, 2));

      if (!data || data.length === 0) {
        console.log('No roles found for admin');
        return {
          data: [],
          error: null,
          success: true
        };
      }

      // Transform data thành format AdminWithRoles
      const roles: AdminWithRoles['roles'] = [];

      data.forEach((adminRole: any) => {
        if (adminRole.roles) {
          const role = adminRole.roles;
          const permissions = role.role_permissions || [];
          
          roles.push({
            id: role.id,
            name: role.name,
            description: role.description,
            permissions: permissions
          });
        }
      });

      console.log('Transformed roles:', JSON.stringify(roles, null, 2));

      return {
        data: roles,
        error: null,
        success: true
      };
    } catch (error: any) {
      console.error('Exception in getAdminRolesAndPermissions:', error);
      return {
        data: null,
        error: error.message,
        success: false
      };
    }
  }

  /**
   * Convert roles-based permissions thành backward compatible format
   */
  static convertRolesToPermissions(adminData: any, roles: AdminWithRoles['roles']): AdminWithPermissions[] {
    const permissions: AdminWithPermissions[] = [];

    roles.forEach(role => {
      role.permissions.forEach(permission => {
        permissions.push({
          admin_id: adminData.id,
          full_name: adminData.full_name,
          user_name: adminData.user_name,
          role_name: role.name,
          module_name: permission.module_name,
          sub_module_name: permission.sub_module_name,
          view_permission: permission.view_permission,
          add_permission: permission.add_permission,
          edit_permission: permission.edit_permission,
          delete_permission: permission.delete_permission
        });
      });
    });

    return permissions;
  }

  /**
   * Kiểm tra quyền của admin
   */
  static checkPermission(
    permissions: AdminWithPermissions[],
    moduleName: string,
    subModuleName: string | null,
    permissionType: 'view' | 'add' | 'edit' | 'delete'
  ): boolean {
    const permission = permissions.find(p => 
      p.module_name === moduleName && 
      p.sub_module_name === subModuleName
    );

    if (!permission) return false;

    switch (permissionType) {
      case 'view':
        return permission.view_permission;
      case 'add':
        return permission.add_permission;
      case 'edit':
        return permission.edit_permission;
      case 'delete':
        return permission.delete_permission;
      default:
        return false;
    }
  }

  /**
   * Nhóm quyền theo module và sub-module
   */
  static groupPermissions(permissions: AdminWithPermissions[]): GroupedPermissions {
    const grouped: GroupedPermissions = {};

    permissions.forEach(permission => {
      const moduleName = permission.module_name;
      const subModuleName = permission.sub_module_name || 'default';

      if (!grouped[moduleName]) {
        grouped[moduleName] = {};
      }

      grouped[moduleName][subModuleName] = {
        module_name: permission.module_name,
        sub_module_name: permission.sub_module_name,
        view_permission: permission.view_permission,
        add_permission: permission.add_permission,
        edit_permission: permission.edit_permission,
        delete_permission: permission.delete_permission
      };
    });

    return grouped;
  }

  /**
   * Lấy danh sách module có quyền truy cập
   */
  static getAccessibleModules(permissions: AdminWithPermissions[]): string[] {
    const modules = new Set<string>();
    
    permissions.forEach(permission => {
      if (permission.view_permission) {
        modules.add(permission.module_name);
      }
    });

    return Array.from(modules);
  }

  /**
   * Kiểm tra admin có quyền truy cập module không
   */
  static hasModuleAccess(permissions: AdminWithPermissions[], moduleName: string): boolean {
    return permissions.some(p => 
      p.module_name === moduleName && p.view_permission
    );
  }

  /**
   * Tạo token đơn giản cho admin (trong thực tế nên dùng JWT)
   */
  private static generateAdminToken(admin: Admin): string {
    return btoa(JSON.stringify({
      id: admin.id,
      user_name: admin.user_name,
      timestamp: Date.now()
    }));
  }

  /**
   * Verify admin token
   */
  static verifyAdminToken(token: string): { valid: boolean; adminId?: string } {
    try {
      const decoded = JSON.parse(atob(token));
      
      // Kiểm tra token có hết hạn không (24 giờ)
      const now = Date.now();
      const tokenTime = decoded.timestamp;
      const twentyFourHours = 24 * 60 * 60 * 1000;

      if (now - tokenTime > twentyFourHours) {
        return { valid: false };
      }

      return { 
        valid: true, 
        adminId: decoded.id 
      };
    } catch (error) {
      return { valid: false };
    }
  }

  /**
   * Lấy thông tin admin hiện tại từ token
   */
  static async getCurrentAdmin(token: string): Promise<AdminApiResponse<AdminLoginResponse>> {
    try {
      const tokenVerification = this.verifyAdminToken(token);
      
      if (!tokenVerification.valid) {
        return {
          data: null,
          error: 'Token không hợp lệ',
          success: false
        };
      }

      const { data: adminData, error: adminError } = await this.findById<Admin>(
        this.adminTableName,
        tokenVerification.adminId!
      );

      if (adminError || !adminData) {
        return {
          data: null,
          error: 'Không tìm thấy thông tin admin',
          success: false
        };
      }

      // Lấy roles và permissions của admin
      const rolesResult = await this.getAdminRolesAndPermissions(adminData.id!);

      if (!rolesResult.success) {
        return {
          data: null,
          error: 'Không thể tải quyền của admin',
          success: false
        };
      }

      // Convert roles thành backward compatible permissions
      const permissions = this.convertRolesToPermissions(adminData, rolesResult.data || []);

      // Loại bỏ password khỏi response
      const { pass_word, ...adminWithoutPassword } = adminData;

      return {
        data: {
          admin: adminWithoutPassword as Admin,
          roles: rolesResult.data || [],
          permissions: permissions,
          token
        },
        error: null,
        success: true
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message,
        success: false
      };
    }
  }

  /**
   * Đăng xuất admin (client-side)
   */
  static logout(): void {
    // Xóa token từ localStorage
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_permissions');
    localStorage.removeItem('admin_info');
  }

  /**
   * Lưu thông tin admin vào localStorage
   */
  static saveAdminSession(loginResponse: AdminLoginResponse): void {
    if (loginResponse.token) {
      localStorage.setItem('admin_token', loginResponse.token);
    }
    localStorage.setItem('admin_permissions', JSON.stringify(loginResponse.permissions));
    localStorage.setItem('admin_info', JSON.stringify(loginResponse.admin));
  }

  /**
   * Lấy thông tin admin từ localStorage
   */
  static getStoredAdminInfo(): {
    token?: string;
    admin?: Admin;
    permissions?: AdminWithPermissions[];
  } {
    try {
      const token = localStorage.getItem('admin_token');
      const admin = localStorage.getItem('admin_info');
      const permissions = localStorage.getItem('admin_permissions');

      return {
        token: token || undefined,
        admin: admin ? JSON.parse(admin) : undefined,
        permissions: permissions ? JSON.parse(permissions) : undefined
      };
    } catch (error) {
      return {};
    }
  }
}
